import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Users } from "lucide-react";
import BulkStudentPicker from "./BulkStudentPicker";
import ImagePicker from "./ImagePicker";

const SLOTS = ["breakfast", "lunch", "snacks"];
const SUBSCRIPTION_TYPES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "term", label: "Term-based" },
];
const TERMS = ["Term I", "Term II", "Term III"];
const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat"];

const today = () => new Date().toISOString().slice(0, 10);

const ACCENTS = {
  subscription: { bg: "bg-slate-900", text: "text-slate-900", border: "border-slate-200", lt: "bg-slate-50" },
  preorder:     { bg: "bg-amber-700",  text: "text-amber-900",  border: "border-amber-200",  lt: "bg-amber-50"  },
  special:      { bg: "bg-rose-700",   text: "text-rose-900",   border: "border-rose-200",   lt: "bg-rose-50"   },
};

export default function CreatePlanModal({ family, existing, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const isEdit = !!existing;
  const initForm = () => {
    if (!existing) return {
      name: "",
      type: family === "preorder" ? "daily" : "monthly",
      price: 0,
      slots: ["lunch"],
      start_date: today(),
      end_date: "",
      term: "Term I",
      cutoff_time: "16:00",
      available_days: ["mon", "tue", "wed", "thu", "fri"],
      description: "",
      scheduled_date: today(),
      display_until: today(),
      capacity: 100,
      image_url: "",
      menu_items: "",
      applicable_grades: "",
      non_veg_option: false,
      items_per_day: 1,
      linked_template_id: "",
      buffet: false,
    };
    // edit pre-fill
    const e = existing;
    const arr = (x) => Array.isArray(x) ? x : [];
    return {
      name: e.name || "",
      type: e.type || (family === "preorder" ? "daily" : "monthly"),
      price: e.price || 0,
      slots: arr(e.slots).length > 0 ? arr(e.slots) : (e.meal_slot ? [e.meal_slot] : ["lunch"]),
      start_date: e.start_date || today(),
      end_date: e.end_date || "",
      term: e.term || "Term I",
      cutoff_time: e.cutoff_time || "16:00",
      available_days: arr(e.available_days).length > 0 ? arr(e.available_days) : ["mon", "tue", "wed", "thu", "fri"],
      description: e.description || "",
      scheduled_date: e.scheduled_date || today(),
      display_until: e.display_until || today(),
      capacity: e.capacity || 100,
      image_url: e.image_url || "",
      menu_items: family === "special"
        ? arr(e.items).map(it => typeof it === "string" ? it : it.name).filter(Boolean).join("\n")
        : arr(e.menu_items).join("\n"),
      applicable_grades: arr(e.applicable_grades).join(", "),
      non_veg_option: !!e.non_veg_option,
      items_per_day: e.items_per_day || 1,
      linked_template_id: e.linked_template_id || "",
      buffet: !!e.buffet,
    };
  };
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  // savedPlan tracks the result of a successful CREATE — used to switch
  // the modal into "assign students" step. NOT set when editing.
  const [savedPlan, setSavedPlan] = useState(null);
  const [excludeIds, setExcludeIds] = useState([]);
  const accents = ACCENTS[family];

  useEffect(() => {
    if (family === "subscription") {
      api.get("/weekly-menu/templates").then(r => setTemplates(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [family]);

  const toggle = (key, val) => setForm(prev => ({
    ...prev,
    [key]: prev[key].includes(val) ? prev[key].filter(v => v !== val) : [...prev[key], val]
  }));
  // Single-slot select (for preorder/special)
  const pickSlot = (val) => setForm(prev => ({ ...prev, slots: [val] }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const menuItems = form.buffet ? [] : form.menu_items.split("\n").map(s => s.trim()).filter(Boolean);
      let saved = null;
      if (family === "special") {
        const payload = {
          name: form.name,
          price: Number(form.price),
          meal_slot: form.slots[0] || "lunch",
          scheduled_date: form.scheduled_date,
          display_until: form.display_until,
          capacity: Number(form.capacity),
          description: form.description,
          image_url: form.image_url,
          items: menuItems.map(n => ({ name: n })),
          buffet: !!form.buffet,
          status: existing?.status || "open",
        };
        if (isEdit) saved = (await api.put(`/specials/${existing.id}`, payload)).data;
        else saved = (await api.post("/specials", payload)).data;
      } else {
        const payload = {
          name: form.name,
          type: form.type,
          price: family === "subscription" ? 0 : Number(form.price),
          slots: form.slots,
          start_date: form.start_date,
          end_date: form.end_date || null,
          term: form.term,
          family,
          cutoff_time: family === "preorder" ? form.cutoff_time : null,
          available_days: family === "preorder" ? form.available_days : null,
          active: existing ? (existing.active !== false) : true,
          description: form.description,
          image_url: form.image_url,
          menu_items: menuItems,
          buffet: !!form.buffet,
          linked_template_id: family === "subscription" ? (form.linked_template_id || null) : null,
          applicable_grades: form.applicable_grades.split(",").map(g => g.trim()).filter(Boolean),
          non_veg_option: family === "subscription" ? !!form.non_veg_option : false,
          items_per_day: Number(form.items_per_day) || 1,
        };
        if (isEdit) saved = (await api.put(`/meal-plans/${existing.id}`, payload)).data;
        else saved = (await api.post("/meal-plans", payload)).data;
      }
      toast.success(isEdit ? "Updated" : "Created");
      onCreated?.();
      // After a fresh CREATE of a subscription plan, switch to the bulk-assign step.
      // On EDIT, close — admin can always assign more via the side panel's Bulk add button.
      if (family === "subscription" && !isEdit && saved?.id) {
        try {
          const r = await api.get(`/meal-plans/${saved.id}/subscribed-students`);
          setExcludeIds(Array.isArray(r.data) ? r.data.map(x => x.student_id) : []);
        } catch { setExcludeIds([]); }
        setSavedPlan(saved);
      } else {
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-2 sm:p-4" data-testid="create-plan-modal">
      <div className={`bg-white rounded-2xl max-w-3xl w-full ${accents.border} border-2 flex flex-col max-h-[95vh] sm:max-h-[90vh]`}>
        {/* Header — sticky */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border bg-white rounded-t-2xl shrink-0">
          <h2 className={`font-heading text-lg sm:text-2xl font-extrabold ${accents.text} capitalize pr-2 truncate`}>
            {savedPlan ? (
              <span className="flex items-center gap-2"><Users size={20} /> <span className="truncate">Assign to {savedPlan.name}</span></span>
            ) : (
              <>{isEdit ? "Edit" : "New"} {family} {family === "special" ? "event" : family === "preorder" ? "menu" : "plan"}</>
            )}
          </h2>
          <button onClick={onClose} data-testid="cp-close" className="p-2 rounded-full hover:bg-cream-200 shrink-0"><X size={18} /></button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
        {savedPlan && family === "subscription" ? (
          <>
            <div className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-700">
              Plan saved. Add students now (skip to do it later from the plan card → Bulk add).
            </div>
            <BulkStudentPicker
              planId={savedPlan.id}
              planStart={savedPlan.start_date}
              planEnd={savedPlan.end_date}
              excludeIds={excludeIds}
              embedded
              onAssigned={(res) => {
                if (res?.assigned > 0) {
                  // refresh exclude list after each batch so the same students don't show up again
                  api.get(`/meal-plans/${savedPlan.id}/subscribed-students`)
                    .then(r => setExcludeIds(Array.isArray(r.data) ? r.data.map(x => x.student_id) : excludeIds))
                    .catch(() => {});
                }
                onCreated?.();
              }}
            />
          </>
        ) : (
          <></>
        )}

        {!savedPlan && (<>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Name</label>
            <input data-testid="cp-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder={family === "preorder" ? "e.g., South Indian Breakfast" : family === "special" ? "e.g., Diwali Feast" : "e.g., Junior Wing Term I"}
              className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
          </div>

          {/* ----- SUBSCRIPTION ----- */}
          {family === "subscription" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Type</label>
                  <select data-testid="cp-type" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm bg-white">
                    {SUBSCRIPTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Term</label>
                  <select data-testid="cp-term" value={form.term} onChange={e => setForm(p => ({ ...p, term: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm bg-white">
                    {TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Validity from</label>
                  <input type="date" data-testid="cp-start" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Validity to</label>
                  <input type="date" data-testid="cp-end" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Linked Weekly Menu (template)</label>
                <select data-testid="cp-template" value={form.linked_template_id} onChange={e => setForm(p => ({ ...p, linked_template_id: e.target.value }))}
                  className="block w-full rounded-xl border border-border px-3 py-2 text-sm bg-white">
                  <option value="">— No menu linked —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <div className="text-[11px] text-ink-500 mt-1">Build templates in Weekly Menu first, then link here.</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Items/day</label>
                  <input type="number" min="1" data-testid="cp-items-per-day" value={form.items_per_day} onChange={e => setForm(p => ({ ...p, items_per_day: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Applicable grades (comma-sep, blank = all)</label>
                  <input data-testid="cp-grades" value={form.applicable_grades} onChange={e => setForm(p => ({ ...p, applicable_grades: e.target.value }))}
                    placeholder="Grade 5, Grade 6, Grade 7"
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" data-testid="cp-nonveg" checked={form.non_veg_option}
                  onChange={e => setForm(p => ({ ...p, non_veg_option: e.target.checked }))} />
                Non-veg lunch enabled
              </label>
              <div className={`p-3 rounded-xl ${accents.lt} text-xs ${accents.text}`}>
                Subscription plans have <strong>no per-meal price</strong> — students subscribe / are assigned, and consume from the linked Weekly Menu for the validity period.
              </div>
            </>
          )}

          {/* ----- PREORDER ----- */}
          {family === "preorder" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Price (₹)</label>
                  <input type="number" data-testid="cp-price" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Cutoff time (prev day)</label>
                  <input type="time" data-testid="cp-cutoff" value={form.cutoff_time} onChange={e => setForm(p => ({ ...p, cutoff_time: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Available days</label>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {DAYS.map(d => (
                    <button key={d} type="button" onClick={() => toggle("available_days", d)}
                      data-testid={`cp-day-${d}`}
                      className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${form.available_days.includes(d) ? "bg-amber-600 text-white" : "bg-cream-200 text-ink-700"}`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ----- SPECIAL ----- */}
          {family === "special" && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Price (₹)</label>
                  <input type="number" data-testid="cp-price" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Capacity</label>
                  <input type="number" data-testid="cp-capacity" value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Event date</label>
                  <input type="date" data-testid="cp-event-date" value={form.scheduled_date} onChange={e => setForm(p => ({ ...p, scheduled_date: e.target.value }))}
                    className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Order cutoff (last bookable date)</label>
                <input type="date" data-testid="cp-expiry" value={form.display_until} onChange={e => setForm(p => ({ ...p, display_until: e.target.value }))}
                  className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
              </div>
            </>
          )}

          {/* ----- COMMON FIELDS: Slot, image, menu items, description ----- */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-500">
              {family === "subscription" ? "Slots covered" : "Meal slot"}
            </label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {SLOTS.map(s => {
                const isActive = form.slots.includes(s);
                return (
                  <button key={s} type="button"
                    onClick={() => family === "subscription" ? toggle("slots", s) : pickSlot(s)}
                    data-testid={`cp-slot-${s}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${isActive ? `${accents.bg} text-white` : "bg-cream-200 text-ink-700"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
            {family !== "subscription" && <div className="text-[11px] text-ink-500 mt-1">Pick exactly one slot for this {family === "preorder" ? "pre-order menu" : "special event"}.</div>}
          </div>

          {family !== "subscription" && (
            <ImagePicker
              accent={family === "preorder" ? "amber" : "rose"}
              value={form.image_url}
              onChange={(url) => setForm(p => ({ ...p, image_url: url }))}
              suggestedPrompt={form.name ? `${form.name} — Indian school canteen meal, top-down photo` : ""}
            />
          )}

          {/* Buffet toggle (all families) */}
          <div className="p-3 rounded-xl bg-cream-100 border border-border">
            <label className="flex items-start gap-2 text-sm text-ink-700 cursor-pointer">
              <input type="checkbox" data-testid="cp-buffet" checked={form.buffet}
                onChange={e => setForm(p => ({ ...p, buffet: e.target.checked }))} />
              <div>
                <div className="font-semibold">Buffet (multiple items, no fixed menu)</div>
                <div className="text-[11px] text-ink-500">Use for open buffets or special events where listing every dish isn&apos;t practical. Skips the menu items section below.</div>
              </div>
            </label>
          </div>

          {!form.buffet && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Menu items (one per line)</label>
              <textarea data-testid="cp-menu-items" rows={4} value={form.menu_items}
                onChange={e => setForm(p => ({ ...p, menu_items: e.target.value }))}
                placeholder={family === "special"
                  ? "Sweet\nSoup\nVeg biryani\nNon-veg fry\nVeg fry\nWhite rice\nSambar\nRasam\nCurd\nIce cream"
                  : family === "preorder"
                    ? "Idli\nPongal\nPoori\nChutney\nSambar"
                    : "Item 1\nItem 2\nItem 3"}
                className="block w-full rounded-xl border border-border px-3 py-2 text-sm font-mono" />
            </div>
          )}

          {(family === "preorder" || family === "special") && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Description</label>
              <textarea data-testid="cp-desc" rows={2} value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
            </div>
          )}
        </div>
        </>
        )}
        </div>

        {/* Footer — sticky */}
        <div className="px-4 sm:px-6 py-3 border-t border-border bg-white rounded-b-2xl shrink-0 flex justify-end gap-2">
          {savedPlan && family === "subscription" ? (
            <button data-testid="cp-done" onClick={onClose}
              className="px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
              Done
            </button>
          ) : (
            <>
              <button onClick={onClose} className="px-5 py-2.5 rounded-full text-sm font-semibold bg-cream-200 text-ink-700 hover:bg-cream-300">Cancel</button>
              <button data-testid="cp-submit" disabled={saving} onClick={submit}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold text-white ${accents.bg} hover:opacity-90 disabled:opacity-50`}>
                {saving ? "Saving…" : family === "subscription" && !isEdit ? "Create & continue" : (isEdit ? "Save changes" : "Create")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
